/**
 * Render loop capped at a low fps (pixel art needs 8–12), paused when the page is hidden.
 * Uses one setTimeout chain instead of rAF at 60 Hz to keep the CPU idle between frames.
 */
export class FrameLoop {
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  lastFrameAt = 0;
  frames = 0;

  constructor(
    private readonly onFrame: (now: number) => void,
    private readonly fps = 10,
    private readonly clock: () => number = () => Date.now(),
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    this.schedule(0);
  }

  stop(): void {
    this.running = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private onVisibility = () => {
    if (document.hidden) {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    } else if (this.running && !this.timer) {
      this.schedule(0);
    }
  };

  private schedule(delay: number) {
    this.timer = setTimeout(this.tick, delay);
  }

  private tick = () => {
    this.timer = null;
    if (!this.running || document.hidden) return;
    const start = this.clock();
    try {
      this.onFrame(start);
    } catch (e) {
      console.error("frame failed", e);
    }
    this.lastFrameAt = start;
    this.frames = (this.frames + 1) % 1_000_000_000;
    const spent = this.clock() - start;
    this.schedule(Math.max(10, 1000 / this.fps - spent));
  };
}
